import { isPlatformBrowser } from '@angular/common';
import { Component, inject, Inject, PLATFORM_ID } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { filter, map, mergeMap } from 'rxjs/operators';
import { SessionService } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/services/session.service';
import { TenantRoleService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/tenant-role/services/tenant-role.service';
import { UsersService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/users/services/users.service';
import { GoogleAnalyticsService } from './features/services/google-analytics/google-analytics.service';
import { SEOService } from './features/services/seo.service';
import { ActivityPingService } from './features/services/activity-ping.service';

@Component({
  standalone: false,
  selector: 'app-root',
  template: '<router-outlet (activate)="onActivate()" />',
})
export class AppComponent {
  private readonly meta = inject(Meta);
  private readonly seoService = inject(SEOService);

  constructor(
    private tenantRoleService: TenantRoleService,
    private angularFireAuth: AngularFireAuth,
    private router: Router,
    private userService: UsersService,
    private sessionService: SessionService,
    private activityPingService: ActivityPingService,
    private ccService: NgcCookieConsentService,
    private activatedRoute: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.angularFireAuth.authState.subscribe(async (user) => {
      if (user) {
        await this.tenantRoleService.loadRole();
        this.activityPingService.start(user.uid);
      } else {
        this.activityPingService.stop();
      }
    });

    router.events.subscribe((event) => {
      if (isPlatformBrowser(this.platformId) && event instanceof NavigationEnd) {
        this.pageView(event.urlAfterRedirects);

        window.scrollTo(0, 0);
        document.body.scrollTop = 0;

        let matDrawer = document.getElementsByTagName('mat-drawer-content');
        let matSidenav = document.getElementsByTagName('mat-sidenav-content');

        if (matDrawer && matDrawer.length) {
          matDrawer[0].scrollTo(0, 0);
        }

        if (matSidenav && matSidenav.length) {
          matSidenav[0].scrollTo(0, 0);
        }
      }

      const userId = this.userService.userSubject?.value?._id;

      if (userId) {
        this.sessionService.handleRouteChange(userId, event);
      }
    });
  }

  ngOnInit() {
    // Cookie consent
    if (isPlatformBrowser(this.platformId)) {
      const dismissed = localStorage.getItem('cookieconsent');

      if (!dismissed) {
        this.ccService.open();
      }
      this.ccService.statusChange$.subscribe((response: any) => {
        if (response.status === 'dismiss') {
          localStorage.setItem('cookieconsent', 'true');
        }
      });
    }

    // TODO: I think this would be better implemented as a route resolver
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe(async (routeData) => {
        // Handle only cases not covered by dynamic route resolver
        if (!routeData.dynamicRoute) {
          this.meta.updateTag({ name: 'description', content: routeData.seo?.description ?? '' });
          this.seoService.setTitle(routeData.seo?.title);
          this.seoService.clearHreflang();
        }
      });
  }

  onActivate() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.scrollTop = 0;
    }
  }

  pageView(urlAfterRedirects) {
    this.googleAnalyticsService.trackPageView(urlAfterRedirects);
  }
}
